(() => {
  if (customElements.get('orbito-product-bundle')) return;

  class OrbitoProductBundle extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      this.form = document.getElementById(this.getAttribute('form-id'));
      this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'), element => ({
        element,
        product: JSON.parse(element.querySelector('[data-bundle-product]').textContent),
        variant: null,
        quantity: 0,
        options: Array.from(element.querySelectorAll('[data-bundle-option]'))
      }));
      this.mainVariant = this.config.variant;
      this.rows.forEach(row => {
        const initial = row.product.variants.find(variant => String(variant.id) === row.element.dataset.selectedVariant);
        this.setOptions(row, initial);
        this.updateRow(row);
      });
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-bundle-choice]')) return;
        const row = this.rows.find(item => item.element.contains(event.target));
        this.updateRow(row);
        this.updateSummary();
      }, { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-bundle-plus], [data-bundle-minus]');
        if (!button || button.disabled || this.busy) return;
        const row = this.rows.find(item => item.element.contains(button));
        const { min, increment } = row.variant;
        row.quantity = button.hasAttribute('data-bundle-plus')
          ? (row.quantity === 0 ? min : row.quantity + increment)
          : (row.quantity <= min ? 0 : row.quantity - increment);
        this.updateQuantity(row);
        this.updateSummary();
      }, { signal });
      this.form?.addEventListener('variant:changed', event => {
        this.mainVariant = event.detail.variant;
        this.rows.forEach(row => this.updateRow(row));
        this.updateSummary();
      }, { signal });
      this.updateSummary();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    normalize(value) {
      return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    isModel(name) {
      return ['model', 'device', 'phone model', '型号', '機型', '机型'].includes(this.normalize(name));
    }

    modelValue() {
      if (!this.hasAttribute('match-model')) return null;
      const index = this.config.options.findIndex(name => this.isModel(name));
      return index < 0 ? null : this.mainVariant?.options?.[index];
    }

    setOptions(row, variant) {
      if (!variant) return;
      row.options.forEach((option, index) => {
        option.querySelectorAll('[data-bundle-choice]').forEach(input => {
          input.checked = this.normalize(input.value) === this.normalize(variant.options[index]);
        });
      });
    }

    updateRow(row) {
      const model = this.modelValue();
      const selected = row.options.map(option => option.querySelector('[data-bundle-choice]:checked')?.value);
      const candidates = row.product.variants.filter(variant => row.options.every((option, index) => {
        return model == null || !this.isModel(option.dataset.optionName)
          || this.normalize(variant.options[index]) === this.normalize(model);
      }));
      row.options.forEach((option, index) => {
        if (model != null && this.isModel(option.dataset.optionName)) selected[index] = model;
      });
      row.variant = candidates.find(variant => variant.options.every((value, index) => this.normalize(value) === this.normalize(selected[index]))) ?? null;
      if (!row.options.length) row.variant = row.product.variants[0] ?? null;
      this.setOptions(row, row.variant);

      row.options.forEach((option, index) => {
        option.classList.toggle('is-model-matched', model != null && this.isModel(option.dataset.optionName));
        option.querySelector('[data-bundle-option-label]').textContent = selected[index] ?? '';
        option.querySelectorAll('[data-bundle-choice]').forEach(input => {
          input.checked = this.normalize(input.value) === this.normalize(selected[index]);
          const exists = candidates.some(variant => variant.available && variant.options.every((value, position) => {
            return this.normalize(value) === this.normalize(position === index ? input.value : selected[position]);
          }));
          input.disabled = this.busy || (model != null && this.isModel(option.dataset.optionName));
          input.parentElement.classList.toggle('is-disabled', !exists);
        });
      });
      const variant = row.variant;
      const purchasable = variant?.available && !row.product.comingSoon && !row.product.requiresSellingPlan;
      row.purchasable = Boolean(purchasable);
      const priceVariant = variant ?? row.product.variants[0];
      if (priceVariant) {
        row.element.querySelector('[data-bundle-price]').innerHTML = priceVariant.price;
        const compare = row.element.querySelector('[data-bundle-compare]');
        compare.innerHTML = priceVariant.compare;
        compare.hidden = !priceVariant.onSale;
      }
      const image = row.element.querySelector('img');
      const imageUrl = variant?.image ?? row.product.image;
      if (image && imageUrl && image.getAttribute('src') !== imageUrl) {
        image.removeAttribute('srcset');
        image.src = imageUrl;
      }
      row.element.querySelectorAll('[data-bundle-link]').forEach(link => {
        link.href = row.product.url + (variant ? `?variant=${variant.id}` : '');
      });
      const strings = this.config.strings;
      row.element.querySelector('[data-bundle-status]').textContent = row.product.comingSoon ? strings.comingSoon
        : row.product.requiresSellingPlan ? strings.sellingPlan
        : !variant ? strings.unavailable : !variant.available ? strings.soldOut : '';
      if (!purchasable) row.quantity = 0;
      if (row.quantity > 0) {
        const min = variant.min;
        const increment = variant.increment;
        row.quantity = Math.max(min, min + Math.floor((row.quantity - min) / increment) * increment);
        const max = this.maxQuantity(row);
        if (max < min) row.quantity = 0;
        else if (row.quantity > max) row.quantity = min + Math.floor((max - min) / increment) * increment;
      }
      this.updateQuantity(row);
    }

    maxQuantity(row) {
      return Math.min(row.variant?.max ?? Infinity, row.variant?.stock ?? Infinity);
    }

    updateQuantity(row) {
      row.element.classList.toggle('is-selected', row.quantity > 0);
      row.element.querySelector('[data-bundle-quantity]').textContent = row.quantity;
      row.element.querySelector('[data-bundle-minus]').disabled = this.busy || row.quantity === 0;
      const next = row.quantity === 0 ? row.variant?.min : row.quantity + row.variant?.increment;
      row.element.querySelector('[data-bundle-plus]').disabled = this.busy || !row.purchasable || next > this.maxQuantity(row);
    }

    updateSummary() {
      const count = this.rows.reduce((total, row) => total + row.quantity, 0);
      const tier = Math.min(count, 3);
      const tokens = { selected: count, remaining: Math.max(3 - count, 0), discount: this.config.discounts[2] + '%' };
      const message = this.querySelector('[data-bundle-message]');
      const interpolate = text => text.replace(/__bundle_(selected|remaining|discount)__/g, (_, key) => tokens[key]);
      if (message) {
        const template = count >= 3 ? this.config.strings.complete
          : count === 1 ? this.config.strings.messageOne : this.config.strings.message;
        message.textContent = interpolate(template);
        if (count >= 3) {
          const unlocked = document.createElement('span');
          unlocked.className = 'orbito-product-bundle__unlocked';
          unlocked.textContent = interpolate(this.config.strings.unlocked);
          message.append(' ', unlocked);
        }
      }
      const progress = this.querySelector('[data-bundle-progress]');
      progress?.setAttribute('aria-valuenow', tier);
      if (progress) progress.firstElementChild.style.width = `${tier / 3 * 100}%`;
      this.querySelectorAll('[data-bundle-tier]').forEach(label => label.classList.toggle('is-active', Number(label.dataset.bundleTier) <= tier));
    }

    get items() {
      return this.rows.filter(row => row.quantity > 0 && row.purchasable).map(row => ({ id: row.variant.id, quantity: row.quantity }));
    }

    appendToFormData(data) {
      const items = this.items;
      if (!items.length) return;
      for (const [name, value] of Array.from(data.entries())) {
        if (name === 'id' || name === 'quantity' || name === 'selling_plan' || name.startsWith('properties[')) {
          data.delete(name);
          data.append(`items[0][${name === 'id' ? 'id' : name === 'quantity' ? 'quantity' : name === 'selling_plan' ? 'selling_plan' : name.replace(/^properties\[(.*)\]$/, 'properties][$1')}]`, value);
        }
      }
      if (!data.has('items[0][quantity]')) data.append('items[0][quantity]', '1');
      items.forEach((item, index) => {
        data.append(`items[${index + 1}][id]`, item.id);
        data.append(`items[${index + 1}][quantity]`, item.quantity);
      });
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(Boolean(busy)));
      this.rows.forEach(row => this.updateRow(row));
    }

    reset() {
      this.rows.forEach(row => { row.quantity = 0; this.updateQuantity(row); });
      this.updateSummary();
    }
  }

  customElements.define('orbito-product-bundle', OrbitoProductBundle);
})();
